import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Download, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useAdminMetrics, type UsageRow } from "@/hooks/useAdminMetrics";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PaddleHealthCard } from "@/components/admin/PaddleHealthCard";
import {
  DateRangePicker,
  presetRange,
  type DateRangeValue,
} from "@/components/admin/DateRangePicker";
import { KpiCard } from "@/components/admin/KpiCard";
import { SpendChart } from "@/components/admin/SpendChart";
import { BreakdownCard } from "@/components/admin/BreakdownCard";
import { ErrorsCard } from "@/components/admin/ErrorsCard";
import { RecentCallsTable } from "@/components/admin/RecentCallsTable";
import { SEO } from "@/components/SEO";
import { formatDistanceToNow } from "date-fns";

const RATE_KEY = "admin_usd_inr_rate";
const COMPARE_KEY = "admin_compare_period";
const DEFAULT_RATE = 83;

const fmtUSD = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 4 });
const fmtINR = (n: number) =>
  n.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });

function pctDelta(current: number, previous: number): number | null {
  if (!Number.isFinite(previous) || previous === 0) return current > 0 ? 100 : null;
  return ((current - previous) / previous) * 100;
}

export default function AdminCosts() {
  const isAdmin = useIsAdmin();

  const [range, setRange] = useState<DateRangeValue>(() => presetRange("30d"));
  const [rate, setRate] = useState<number>(() => {
    if (typeof window === "undefined") return DEFAULT_RATE;
    const stored = window.localStorage.getItem(RATE_KEY);
    const parsed = stored ? Number(stored) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_RATE;
  });
  const [compare, setCompare] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem(COMPARE_KEY) !== "false";
  });
  const [callTypeFilter, setCallTypeFilter] = useState<string | null>(null);
  const [modelFilter, setModelFilter] = useState<string | null>(null);
  const [emails, setEmails] = useState<Record<string, string | null>>({});
  const [loadingEmails, setLoadingEmails] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem(RATE_KEY, String(rate));
  }, [rate]);
  useEffect(() => {
    if (typeof window !== "undefined")
      window.localStorage.setItem(COMPARE_KEY, String(compare));
  }, [compare]);

  const { current, previous, loading, error, refresh, lastUpdated } = useAdminMetrics(
    range.from,
    range.to,
    isAdmin === true,
  );

  // Apply page-level filter (call_type / model selected from breakdown).
  const filteredUsage = useMemo(() => {
    return current.usage.filter((r) => {
      if (callTypeFilter && r.call_type !== callTypeFilter) return false;
      if (modelFilter && r.model !== modelFilter) return false;
      return true;
    });
  }, [current.usage, callTypeFilter, modelFilter]);

  // KPIs (current period — using filtered usage so filter cascades).
  const kpis = useMemo(() => {
    const spend = filteredUsage.reduce((s, r) => s + Number(r.estimated_cost_usd ?? 0), 0);
    const revenue = current.orders.reduce((s, o) => s + (o.amount_cents ?? 0), 0) / 100;
    const tryons = current.tryons.length;
    const activeUsers = new Set(filteredUsage.map((r) => r.user_id).filter(Boolean)).size;
    const costPerTryon = tryons ? spend / tryons : 0;
    const margin = revenue ? ((revenue - spend) / revenue) * 100 : 0;
    const marginUsd = revenue - spend;

    const prevSpend = previous.usage.reduce((s, r) => s + Number(r.estimated_cost_usd ?? 0), 0);
    const prevRevenue =
      previous.orders.reduce((s, o) => s + (o.amount_cents ?? 0), 0) / 100;
    const prevTryons = previous.tryons.length;
    const prevActiveUsers = new Set(previous.usage.map((r) => r.user_id).filter(Boolean)).size;
    const prevCostPerTryon = prevTryons ? prevSpend / prevTryons : 0;
    const prevMargin = prevRevenue ? ((prevRevenue - prevSpend) / prevRevenue) * 100 : 0;

    return {
      spend,
      revenue,
      tryons,
      activeUsers,
      costPerTryon,
      margin,
      marginUsd,
      deltas: {
        spend: pctDelta(spend, prevSpend),
        revenue: pctDelta(revenue, prevRevenue),
        tryons: pctDelta(tryons, prevTryons),
        activeUsers: pctDelta(activeUsers, prevActiveUsers),
        costPerTryon: pctDelta(costPerTryon, prevCostPerTryon),
        margin: prevMargin === 0 ? null : margin - prevMargin, // pp difference
      },
    };
  }, [filteredUsage, current, previous]);

  // Top users (cost from filtered usage, revenue from orders).
  const topUsers = useMemo(() => {
    const map = new Map<
      string,
      { user_id: string; cost: number; calls: number; revenue: number }
    >();
    for (const r of filteredUsage) {
      if (!r.user_id) continue;
      const cur = map.get(r.user_id) ?? {
        user_id: r.user_id,
        cost: 0,
        calls: 0,
        revenue: 0,
      };
      cur.cost += Number(r.estimated_cost_usd ?? 0);
      cur.calls += 1;
      map.set(r.user_id, cur);
    }
    for (const o of current.orders) {
      const cur = map.get(o.user_id) ?? {
        user_id: o.user_id,
        cost: 0,
        calls: 0,
        revenue: 0,
      };
      cur.revenue += o.amount_cents / 100;
      map.set(o.user_id, cur);
    }
    return Array.from(map.values())
      .sort((a, b) => b.cost - a.cost)
      .slice(0, 20);
  }, [filteredUsage, current.orders]);

  // Fetch emails for top users (admin-only).
  useEffect(() => {
    if (!isAdmin || topUsers.length === 0) return;
    const missing = topUsers.map((u) => u.user_id).filter((id) => !(id in emails));
    if (missing.length === 0) return;
    setLoadingEmails(true);
    supabase.functions
      .invoke("admin-user-emails", { body: { userIds: missing.slice(0, 50) } })
      .then(({ data }) => {
        const list = (data as { users?: { user_id: string; email: string | null }[] })?.users ?? [];
        setEmails((prev) => {
          const next = { ...prev };
          for (const u of list) next[u.user_id] = u.email;
          for (const id of missing) if (!(id in next)) next[id] = null;
          return next;
        });
      })
      .finally(() => setLoadingEmails(false));
  }, [topUsers, isAdmin, emails]);

  if (isAdmin === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!isAdmin) return <Navigate to="/app" replace />;

  const exportAll = () => {
    const sections: string[] = [];
    sections.push(`# Admin cost dashboard export · ${new Date().toISOString()}`);
    sections.push(`# Range: ${range.from.toISOString()} → ${range.to.toISOString()}`);
    sections.push("");
    sections.push("## KPIs");
    sections.push(
      toCSV([
        { metric: "spend_usd", value: kpis.spend.toFixed(6) },
        { metric: "revenue_usd", value: kpis.revenue.toFixed(2) },
        { metric: "margin_usd", value: kpis.marginUsd.toFixed(2) },
        { metric: "margin_pct", value: kpis.margin.toFixed(2) },
        { metric: "tryons", value: kpis.tryons },
        { metric: "cost_per_tryon_usd", value: kpis.costPerTryon.toFixed(6) },
        { metric: "active_users", value: kpis.activeUsers },
      ]),
    );
    sections.push("");
    sections.push("## Top users");
    sections.push(
      toCSV(
        topUsers.map((u) => ({
          user_id: u.user_id,
          email: emails[u.user_id] ?? "",
          calls: u.calls,
          cost_usd: u.cost.toFixed(6),
          revenue_usd: u.revenue.toFixed(2),
          margin_usd: (u.revenue - u.cost).toFixed(2),
        })),
      ),
    );
    sections.push("");
    sections.push("## All usage rows");
    sections.push(
      toCSV(
        filteredUsage.map((r) => ({
          created_at: r.created_at,
          user_id: r.user_id ?? "",
          model: r.model,
          call_type: r.call_type,
          input_tokens: r.input_tokens,
          output_tokens: r.output_tokens,
          status: r.status,
          error: r.error ?? "",
          cost_usd: Number(r.estimated_cost_usd ?? 0).toFixed(6),
        })),
      ),
    );
    downloadCSV(`admin-costs-${todayStr()}.csv`, sections.join("\n"));
  };

  const exportRecentCalls = (rows: UsageRow[]) => {
    downloadCSV(
      `recent-calls-${todayStr()}.csv`,
      toCSV(
        rows.map((r) => ({
          created_at: r.created_at,
          user_id: r.user_id ?? "",
          model: r.model,
          call_type: r.call_type,
          input_tokens: r.input_tokens,
          output_tokens: r.output_tokens,
          status: r.status,
          error: r.error ?? "",
          cost_usd: Number(r.estimated_cost_usd ?? 0).toFixed(6),
          cost_inr: (Number(r.estimated_cost_usd ?? 0) * rate).toFixed(4),
        })),
      ),
    );
  };

  const activeFilterChips: Array<{ label: string; clear: () => void }> = [];
  if (callTypeFilter)
    activeFilterChips.push({
      label: `call_type: ${callTypeFilter}`,
      clear: () => setCallTypeFilter(null),
    });
  if (modelFilter)
    activeFilterChips.push({ label: `model: ${modelFilter}`, clear: () => setModelFilter(null) });

  return (
    <>
      <SEO title="Admin Costs · FitAura AI" description="Internal cost dashboard." noindex />
      <div className="relative min-h-screen overflow-hidden bg-background">
        <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
          <div className="absolute -left-32 top-0 h-[420px] w-[420px] rounded-full bg-primary/10 blur-[140px]" />
        </div>

        <header className="sticky top-0 z-10 border-b border-white/10 bg-background/80 backdrop-blur-xl">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-3">
            <Button asChild size="icon" variant="ghost">
              <Link to="/app/profile" aria-label="Back">
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </Button>
            <div className="min-w-0">
              <span className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-primary">
                — ADMIN
              </span>
              <h1 className="font-display truncate text-base font-bold uppercase tracking-[0.18em]">
                AI Cost Dashboard
              </h1>
            </div>

            <div className="ml-auto flex flex-wrap items-center gap-2">
              <DateRangePicker value={range} onChange={setRange} />
              <div className="flex items-center gap-1.5 rounded border px-2 py-1 text-xs">
                <Switch
                  id="compare-toggle"
                  checked={compare}
                  onCheckedChange={setCompare}
                  className="scale-75"
                />
                <Label htmlFor="compare-toggle" className="cursor-pointer text-xs">
                  Compare
                </Label>
              </div>
              <label
                htmlFor="usd-inr-rate"
                className="flex items-center gap-1.5 text-xs text-muted-foreground"
              >
                <span className="whitespace-nowrap">$1 =</span>
                <Input
                  id="usd-inr-rate"
                  name="usdInrRate"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  value={rate}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setRate(Number.isFinite(v) && v > 0 ? v : DEFAULT_RATE);
                  }}
                  className="h-8 w-16 text-xs"
                />
                <span>₹</span>
              </label>
              <Button asChild size="sm" variant="outline">
                <Link to="/admin/cloud">Cloud →</Link>
              </Button>
              <Button size="sm" variant="outline" onClick={refresh} disabled={loading}>
                <RefreshCw className={loading ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
              </Button>
              <Button size="sm" variant="outline" onClick={exportAll} disabled={loading}>
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Export</span>
              </Button>
            </div>
          </div>
          {(lastUpdated || activeFilterChips.length > 0) && (
            <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 px-4 pb-2 text-[11px] text-muted-foreground">
              <span>
                {lastUpdated
                  ? `Updated ${formatDistanceToNow(lastUpdated, { addSuffix: true })}`
                  : ""}
              </span>
              {activeFilterChips.length > 0 ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span>Filters:</span>
                  {activeFilterChips.map((c) => (
                    <button
                      key={c.label}
                      onClick={c.clear}
                      className="rounded-full border bg-muted px-2 py-0.5 text-[10px] hover:bg-muted/70"
                    >
                      {c.label} ×
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          )}
        </header>

        <main className="mx-auto max-w-7xl space-y-6 p-4">
          {error ? (
            <Card className="border-destructive">
              <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
            </Card>
          ) : null}

          {loading && current.usage.length === 0 ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* KPI strip */}
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
                <KpiCard
                  label="Spend"
                  value={fmtUSD(kpis.spend)}
                  sub={fmtINR(kpis.spend * rate)}
                  delta={kpis.deltas.spend}
                  invertDelta
                  showCompare={compare}
                />
                <KpiCard
                  label="Revenue"
                  value={fmtUSD(kpis.revenue)}
                  sub={fmtINR(kpis.revenue * rate)}
                  delta={kpis.deltas.revenue}
                  showCompare={compare}
                />
                <KpiCard
                  label="Gross margin"
                  value={`${kpis.margin.toFixed(1)}%`}
                  sub={fmtUSD(kpis.marginUsd)}
                  delta={kpis.deltas.margin}
                  showCompare={compare}
                />
                <KpiCard
                  label="Try-ons"
                  value={kpis.tryons.toLocaleString()}
                  delta={kpis.deltas.tryons}
                  showCompare={compare}
                />
                <KpiCard
                  label="Cost / try-on"
                  value={fmtUSD(kpis.costPerTryon)}
                  sub={fmtINR(kpis.costPerTryon * rate)}
                  delta={kpis.deltas.costPerTryon}
                  invertDelta
                  showCompare={compare}
                />
                <KpiCard
                  label="Active users"
                  value={kpis.activeUsers.toLocaleString()}
                  delta={kpis.deltas.activeUsers}
                  showCompare={compare}
                />
              </div>

              {/* Spend over time */}
              <SpendChart
                usage={filteredUsage}
                tryons={current.tryons}
                from={range.from}
                to={range.to}
                rate={rate}
                onCallTypeClick={(ct) =>
                  setCallTypeFilter((cur) => (cur === ct ? null : ct))
                }
              />

              {/* Breakdowns */}
              <div className="grid gap-4 lg:grid-cols-2">
                <BreakdownCard
                  title="By call type"
                  usage={current.usage}
                  groupBy="call_type"
                  activeKey={callTypeFilter}
                  onSelect={setCallTypeFilter}
                />
                <BreakdownCard
                  title="By model"
                  usage={current.usage}
                  groupBy="model"
                  activeKey={modelFilter}
                  onSelect={setModelFilter}
                />
              </div>

              {/* Operational health */}
              <div className="grid gap-4 lg:grid-cols-2">
                <ErrorsCard usage={current.usage} />
                <PaddleHealthCard />
              </div>

              {/* Top users */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Top users {loadingEmails ? "(loading emails…)" : ""}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {topUsers.length === 0 ? (
                    <p className="py-4 text-center text-sm text-muted-foreground">No usage.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                          <tr>
                            <th className="pb-2">User</th>
                            <th className="pb-2 text-right">Calls</th>
                            <th className="pb-2 text-right">Cost</th>
                            <th className="pb-2 text-right">Revenue</th>
                            <th className="pb-2 text-right">Margin</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topUsers.map((u) => {
                            const margin = u.revenue - u.cost;
                            return (
                              <tr key={u.user_id} className="border-t">
                                <td className="py-2">
                                  <div className="flex flex-col">
                                    <span className="truncate text-[11px]">
                                      {emails[u.user_id] ?? "—"}
                                    </span>
                                    <span className="font-mono text-[9px] text-muted-foreground">
                                      {u.user_id.slice(0, 18)}…
                                    </span>
                                  </div>
                                </td>
                                <td className="py-2 text-right tabular-nums">{u.calls}</td>
                                <td className="py-2 text-right tabular-nums">
                                  {fmtUSD(u.cost)}
                                </td>
                                <td className="py-2 text-right tabular-nums">
                                  {u.revenue > 0 ? fmtUSD(u.revenue) : "—"}
                                </td>
                                <td
                                  className={
                                    "py-2 text-right tabular-nums " +
                                    (margin > 0
                                      ? "text-emerald-600"
                                      : margin < 0
                                      ? "text-destructive"
                                      : "text-muted-foreground")
                                  }
                                >
                                  {u.revenue > 0 ? fmtUSD(margin) : "—"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent calls table */}
              <RecentCallsTable
                usage={filteredUsage}
                rate={rate}
                onExport={exportRecentCalls}
              />
            </>
          )}
        </main>
      </div>
    </>
  );
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function csvEscape(v: unknown): string {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function toCSV(rows: Array<Record<string, unknown>>): string {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const lines = [headers.join(",")];
  for (const row of rows) {
    lines.push(headers.map((h) => csvEscape(row[h])).join(","));
  }
  return lines.join("\n");
}

function downloadCSV(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
